import { useState } from 'react';
import {
  EXCLUSION_REASON_LABEL,
  type CampaignRecord,
  type UnreachableAudienceMember,
  type UnreachableAudiencePage,
} from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { channelDataLabel } from '../api/campaignWizardRules';

export function CampaignUnreachablePanel({
  campaign,
  page,
  canReevaluate,
  onReevaluated,
  onError,
}: {
  campaign: CampaignRecord;
  page: UnreachableAudiencePage;
  canReevaluate: boolean;
  onReevaluated: () => void;
  onError: (message: string) => void;
}) {
  const [editing, setEditing] = useState<UnreachableAudienceMember | null>(null);
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [reevaluating, setReevaluating] = useState(false);
  const channelLabel = channelDataLabel(campaign.channelKind);

  const saveEmail = async () => {
    if (!editing) {
      return;
    }
    setSaving(true);
    try {
      await contactAgentApi.updateContact(editing.contactId, { email: email.slice(0, 255) });
      if (canReevaluate) {
        await contactAgentApi.reevaluateAudience(campaign.id);
      }
      setEditing(null);
      onReevaluated();
    } catch (err) {
      onError(contactAgentError(err));
    } finally {
      setSaving(false);
    }
  };

  const reevaluate = async () => {
    setReevaluating(true);
    try {
      await contactAgentApi.reevaluateAudience(campaign.id);
      onReevaluated();
    } catch (err) {
      onError(contactAgentError(err));
    } finally {
      setReevaluating(false);
    }
  };

  if (page.total === 0) {
    return null;
  }

  return (
    <>
      <Card className='rounded-xl border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'>
        <CardHeader className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
          <div>
            <CardTitle>Les falta el dato de contacto</CardTitle>
            <p className='mt-1 text-sm text-muted-foreground'>
              Esta campaña sale por {channelLabel}. Estos contactos del segmento no tienen{' '}
              {channelLabel} y no se van a notificar hasta que lo completes.
            </p>
          </div>
          <div className='flex flex-wrap gap-2'>
            <Button type='button' variant='outline' size='sm' onClick={() => downloadUnreachableCsv(campaign.id, page.items)}>
              Exportar CSV
            </Button>
            {canReevaluate ? (
              <Button type='button' variant='outline' size='sm' disabled={reevaluating} onClick={() => void reevaluate()}>
                {reevaluating ? 'Reevaluando...' : 'Reevaluar audiencia'}
              </Button>
            ) : (
              <p className='text-xs text-muted-foreground'>La audiencia activa está congelada.</p>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className='overflow-auto rounded-xl border bg-background'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b text-left'>
                  <th className='px-3 py-2'>Contacto</th>
                  <th className='px-3 py-2'>Telefono</th>
                  <th className='px-3 py-2'>Email</th>
                  <th className='px-3 py-2'>Sucursal</th>
                  <th className='px-3 py-2'>Motivo</th>
                  <th className='px-3 py-2' />
                </tr>
              </thead>
              <tbody>
                {page.items.map(item => (
                  <tr key={item.contactId} className='border-b last:border-0'>
                    <td className='px-3 py-2'>
                      {item.businessName || `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim() || 'Contacto'}
                    </td>
                    <td className='px-3 py-2 text-muted-foreground'>{item.phoneMasked}</td>
                    <td className='px-3 py-2 text-muted-foreground'>{item.emailMasked ?? '—'}</td>
                    <td className='px-3 py-2 text-muted-foreground'>{item.branch ?? '—'}</td>
                    <td className='px-3 py-2 text-muted-foreground'>
                      {EXCLUSION_REASON_LABEL[item.exclusionReason]}
                    </td>
                    <td className='px-3 py-2 text-right'>
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        onClick={() => {
                          setEditing(item);
                          setEmail('');
                        }}
                      >
                        Completar {channelLabel}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Dialog open={Boolean(editing)} onOpenChange={open => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Completar dato de contacto</DialogTitle>
            <DialogDescription>
              El listado muestra datos enmascarados. Ingresa el {channelLabel} real del contacto.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            <Label htmlFor='contact-email'>Email</Label>
            <Input
              id='contact-email'
              value={email}
              maxLength={255}
              onChange={event => setEmail(event.target.value)}
              placeholder='correo@empresa.com'
            />
          </div>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => setEditing(null)}>
              Cancelar
            </Button>
            <Button type='button' disabled={saving || !email.trim()} onClick={() => void saveEmail()}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function downloadUnreachableCsv(campaignId: string, items: UnreachableAudienceMember[]) {
  const header = 'contacto,telefono,email,sucursal,motivo';
  const rows = items.map(item =>
    [
      csvCell(item.businessName || `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim() || 'Contacto'),
      csvCell(item.phoneMasked),
      csvCell(item.emailMasked ?? ''),
      csvCell(item.branch ?? ''),
      csvCell(EXCLUSION_REASON_LABEL[item.exclusionReason]),
    ].join(','),
  );
  const blob = new Blob([`${header}\n${rows.join('\n')}\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `faltantes-campaña-${campaignId.slice(0, 64)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function csvCell(value: string): string {
  const bounded = value.slice(0, 256);
  if (bounded.includes(',') || bounded.includes('"') || bounded.includes('\n')) {
    return `"${bounded.replace(/"/g, '""')}"`;
  }
  return bounded;
}
